import React, {useState} from "react";
import {EmployeeDashboardProps} from "@widgets/timeRegistration/EmployeeDashboard";
import {EmployeeStatus} from "@/data/entities/employee";
import {useElapsedTime} from "@widgets/timeRegistration/authenticated/clocking/UseElapsedTime";
import {formatDateTime} from "@/utils/DateUtils";
import {FormattedMessage, useIntl} from "react-intl";
import {Button, useNotifications} from "peggirkit";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger} from "@/utils/NotifyUtils";
import {api} from "@/data/api";

type Props = EmployeeDashboardProps & {
    employee: EmployeeStatus,
};

const ClockOutOption = ({setEmployee, employee}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {hours, minutes, seconds} = useElapsedTime(new Date(employee.timeEntries[0].clockIn));
    const [loading, setLoading] = useState(false);
    const {mutate} = useMutation({
        mutationFn: (code: string) => api.employee.clockOut(code),
        onSuccess: (updatedEmployee: EmployeeStatus) => setEmployee(updatedEmployee),
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
        onSettled: () => setLoading(false)
    });

    return (
        <div className={"flex flex-col gap-6"}>
            <div className={"w-full flex flex-col text-center gap-4 bg-gray-800/5 dark:bg-gray-100/5 p-6 rounded-xl"}>
                <h2 className={"text-sm uppercase text-gray-500 dark:text-gray-400"}>
                    <FormattedMessage id={"clockedInTime"}/>
                </h2>

                <time className={"text-3xl font-mono tabular-nums"}>
                    <FormattedMessage
                        id={"formattedClockedTime"}
                        values={{hours, minutes, seconds}}
                    />
                </time>

                <small className={"text-xs text-gray-500 dark:text-gray-400"}>
                    <FormattedMessage
                        id={"clockedInOn"}
                        values={{date: formatDateTime(employee.timeEntries[0].clockIn)}}
                    />
                </small>
            </div>

            <Button
                buttonAction={{
                    as: "button",
                    actionProps: {
                        onClick: () => mutate(employee.code),
                    },
                }}
                align={"center"}
                width={"w-full"}
                color={"danger"}
                size={"lg"}
                loading={loading}
            >
                <FormattedMessage id={"clockOut"}/>
            </Button>
        </div>
    );
};

export default ClockOutOption;